import React, { useEffect, useState } from "react";
import { studentFeeReportMonthWise } from "../../../api/Student";
import { SweetAlertShow } from "../../master/Master";

const MODULE_CONFIG = [
  { key: "batch_fee", label: "Batch Fee", icon: "ri-graduation-cap-line", color: "primary" },
  { key: "hostel_fee", label: "Hostel Fee", icon: "ri-hotel-bed-line", color: "success" },
  { key: "transport_fee", label: "Transport Fee", icon: "ri-bus-line", color: "warning" },
  { key: "library_fee", label: "Library Fee", icon: "ri-book-2-line", color: "info" },
];

export default function StudentFeeReportMonthWise({
  filters,
  setLoading,
  inr,
  buildQuery,
}) {
  const [data, setData] = useState({});

  const getStudentFeeReportMonthWise = async () => {
    setLoading(true);
    try {
      const response = await studentFeeReportMonthWise(buildQuery());
      if (response.data.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.response?.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentFeeReportMonthWise();
  }, [filters]);

  const num = (v) => {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };

  const months = data?.month_wise || [];
  const grandTotal = data?.grand_total || {};
  const overall = grandTotal?.total || {};

  return (
    <div>
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-3">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between px-4 py-3 border-bottom flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-3 d-flex align-items-center justify-content-center text-white"
              style={{
                width: 44,
                height: 44,
                background: "linear-gradient(135deg, #4f46e5, #6366f1)",
              }}
            >
              <i className="ri-calendar-2-line fs-5"></i>
            </div>
            <div>
              <h6 className="m-0 fw-bold">Month Wise Fee Report</h6>
              <small className="text-muted">
                Module wise payment breakdown grouped by month
              </small>
            </div>
          </div>

          <span className="badge bg-light text-dark border fw-semibold px-3 py-2 rounded-pill">
            <i className="ri-time-line me-1"></i>
            {months.length} Month{months.length === 1 ? "" : "s"}
          </span>
        </div>

        {/* Summary strip — module wise grand totals */}
        <div className="row g-0 border-bottom">
          {MODULE_CONFIG.map((m, idx) => (
            <div
              key={m.key}
              className={`col-6 col-md-2 px-4 py-3 ${
                idx < MODULE_CONFIG.length ? "border-end" : ""
              }`}
            >
              <small className="text-muted text-uppercase fw-semibold d-flex align-items-center gap-1">
                <i className={`${m.icon} text-${m.color}`}></i> {m.label}
              </small>
              <h6 className="m-0 fw-bold mt-1">
                {inr(num(grandTotal?.[m.key]?.total_amount))}
              </h6>
              <small className="text-muted">
                {num(grandTotal?.[m.key]?.transactions)} txns
              </small>
            </div>
          ))}

          <div className="col-12 col-md-4 px-4 py-3 bg-primary-subtle">
            <small className="text-primary text-uppercase fw-semibold d-flex align-items-center gap-1">
              <i className="ri-wallet-3-line"></i> Grand Total
            </small>
            <h4 className="m-0 fw-bold mt-1 text-primary">
              {inr(num(overall?.total_amount))}
            </h4>
            <small className="text-muted">
              {num(overall?.students)} students &middot; {num(overall?.transactions)} txns
            </small>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr className="text-uppercase small text-muted">
                <th className="ps-4 py-3 fw-semibold" style={{ letterSpacing: "0.03em" }}>
                  Month
                </th>
                {MODULE_CONFIG.map((m) => (
                  <th
                    key={m.key}
                    className="text-end py-3 fw-semibold"
                    style={{ letterSpacing: "0.03em" }}
                  >
                    <span className={`text-${m.color}`}>
                      <i className={`${m.icon} me-1`}></i>
                      {m.label}
                    </span>
                  </th>
                ))}
                <th
                  className="text-end pe-4 py-3 fw-semibold"
                  style={{ letterSpacing: "0.03em" }}
                >
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {!months.length ? (
                <tr>
                  <td colSpan={MODULE_CONFIG.length + 2} className="text-center text-muted py-5">
                    <i className="ri-inbox-line fs-2 d-block mb-2 text-secondary"></i>
                    No data available
                  </td>
                </tr>
              ) : (
                months.map((row, idx) => (
                  <tr key={idx}>
                    <td className="ps-4 py-3">
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center fw-bold"
                          style={{ width: 32, height: 32, fontSize: "0.75rem" }}
                        >
                          {row.month?.slice(0, 3)}
                        </span>
                        <span className="fw-semibold">{row.month}</span>
                      </div>
                    </td>

                    {MODULE_CONFIG.map((m) => {
                      const cell = row?.[m.key];
                      const amount = num(cell?.total_amount);
                      return (
                        <td key={m.key} className="text-end py-3">
                          {amount > 0 ? (
                            <>
                              <div className="fw-semibold">{inr(amount)}</div>
                              <small className="text-muted">
                                {num(cell?.transactions)} txns
                              </small>
                            </>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      );
                    })}

                    <td className="text-end pe-4 py-3">
                      <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fw-bold fs-6">
                        {inr(num(row?.total?.total_amount))}
                      </span>
                      <small className="text-muted d-block mt-1">
                        {num(row?.total?.transactions)} txns
                      </small>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

            {!!months.length && (
              <tfoot>
                <tr style={{ background: "#f8f9fc" }}>
                  <td className="ps-4 py-3 fw-bold">Grand Total</td>

                  {MODULE_CONFIG.map((m) => (
                    <td key={m.key} className="text-end py-3 fw-bold">
                      {inr(num(grandTotal?.[m.key]?.total_amount))}
                    </td>
                  ))}

                  <td className="text-end pe-4 py-3 fw-bold text-primary fs-6">
                    {inr(num(overall?.total_amount))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}